// Helper para importar contenido de los archivos markdown
import i18next from 'i18next'
import type { TFunction } from 'i18next'
import intro_es from '../../docs/knowledge-base/01-introduccion-tempora.md?raw'
import intro_en from '../../docs/knowledge-base/en/01-introduction-to-tempora.md?raw'
import guiaTracker_es from '../../docs/knowledge-base/02-guia-usuario-tracker.md?raw'
import guiaTracker_en from '../../docs/knowledge-base/en/02-timer-user-guide.md?raw'
import proyectosTareas_es from '../../docs/knowledge-base/03-gestion-proyectos-tareas.md?raw'
import proyectosTareas_en from '../../docs/knowledge-base/en/03-projects-tasks-management.md?raw'
import organizaciones_es from '../../docs/knowledge-base/04-organizaciones-equipo.md?raw'
import organizaciones_en from '../../docs/knowledge-base/en/04-organizations-team.md?raw'
import reportes_es from '../../docs/knowledge-base/05-reportes-facturacion.md?raw'
import reportes_en from '../../docs/knowledge-base/en/05-reports-billing.md?raw'
import configuracion_es from '../../docs/knowledge-base/06-configuracion-seguridad.md?raw'
import configuracion_en from '../../docs/knowledge-base/en/06-settings-security.md?raw'
import faqs_es from '../../docs/knowledge-base/07-faq-soporte.md?raw'
import faqs_en from '../../docs/knowledge-base/en/07-faq-support.md?raw'
import dashboard_es from '../../docs/knowledge-base/08-dashboard-inicio.md?raw'
import dashboard_en from '../../docs/knowledge-base/en/08-dashboard-overview.md?raw'
import clientes_es from '../../docs/knowledge-base/09-gestion-clientes.md?raw'
import clientes_en from '../../docs/knowledge-base/en/09-client-management.md?raw'
import facturacion_es from '../../docs/knowledge-base/10-planes-facturacion.md?raw'
import facturacion_en from '../../docs/knowledge-base/en/10-plans-billing.md?raw'
import asistentIAPRo_es from '../../docs/knowledge-base/11-asistente-ia-pro.md?raw'
import asistentIAPRo_en from '../../docs/knowledge-base/en/11-ai-assistant-pro.md?raw'
import apiPublica_es from '../../docs/knowledge-base/12-api-publica-webhooks.md?raw'
import apiPublica_en from '../../docs/knowledge-base/en/12-public-api-webhooks.md?raw'
import gamificacion_es from '../../docs/knowledge-base/13-gamificacion-rachas-notificaciones.md?raw'
import gamificacion_en from '../../docs/knowledge-base/en/13-gamification-streaks-notifications.md?raw'

const getContent = (esContent: string, enContent?: string) => {
  const lang = i18next.language || 'es'
  if (lang.startsWith('en') && enContent) return enContent
  return esContent
}

export interface HelpArticle {
  id: string
  title: string
  description: string
  icon: string
  content: string
  category: string
}

export interface HelpCategory {
  slug: string
  title: string
  description: string
}

// Definición de artículos por categoría
export const getHelpArticles = (t: TFunction): Record<string, HelpArticle[]> => ({
  'primeros-pasos': [
    {
      id: 'introduccion',
      title: t('help.categories.gettingStarted.articles.overview.title'),
      description: t('help.categories.gettingStarted.articles.overview.description'),
      icon: '🚀',
      content: getContent(intro_es, intro_en),
      category: 'primeros-pasos'
    },
    {
      id: 'asistente-ia-pro',
      title: t('help.categories.gettingStarted.articles.jarvis.title'),
      description: t('help.categories.gettingStarted.articles.jarvis.description'),
      icon: '✨',
      content: getContent(asistentIAPRo_es, asistentIAPRo_en),
      category: 'primeros-pasos'
    }
  ],
  'dashboard': [
    {
      id: 'vision-dashboard',
      title: t('help.categories.dashboard.articles.view.title'),
      description: t('help.categories.dashboard.articles.view.description'),
      icon: '📊',
      content: getContent(dashboard_es, dashboard_en),
      category: 'dashboard'
    },
    {
      id: 'gamificacion-rachas',
      title: t('help.categories.dashboard.articles.gamification.title'),
      description: t('help.categories.dashboard.articles.gamification.description'),
      icon: '🔥',
      content: getContent(gamificacion_es, gamificacion_en),
      category: 'dashboard'
    }
  ],
  'tracking-tiempo': [
    {
      id: 'usar-timer',
      title: t('help.categories.timeTracking.articles.timer.title'),
      description: t('help.categories.timeTracking.articles.timer.description'),
      icon: '⏱️',
      content: getContent(guiaTracker_es, guiaTracker_en),
      category: 'tracking-tiempo'
    }
  ],
  'gestion-proyectos': [
    {
      id: 'proyectos-tareas',
      title: t('help.categories.projectManagement.articles.projects.title'),
      description: t('help.categories.projectManagement.articles.projects.description'),
      icon: '📁',
      content: getContent(proyectosTareas_es, proyectosTareas_en),
      category: 'gestion-proyectos'
    }
  ],
  'gestion-clientes': [
    {
      id: 'clientes-contactos',
      title: t('help.categories.clientManagement.articles.clients.title'),
      description: t('help.categories.clientManagement.articles.clients.description'),
      icon: '👤',
      content: getContent(clientes_es, clientes_en),
      category: 'gestion-clientes'
    }
  ],
  'reportes-analisis': [
    {
      id: 'reportes-facturacion',
      title: t('help.categories.reportsAnalytics.articles.reports.title'),
      description: t('help.categories.reportsAnalytics.articles.reports.description'),
      icon: '📊',
      content: getContent(reportes_es, reportes_en),
      category: 'reportes-analisis'
    }
  ],
  'solucion-problemas': [
    {
      id: 'faqs',
      title: t('help.categories.troubleshooting.articles.faqs.title'),
      description: t('help.categories.troubleshooting.articles.faqs.description'),
      icon: '❓',
      content: getContent(faqs_es, faqs_en),
      category: 'solucion-problemas'
    }
  ],
  'administracion': [
    {
      id: 'organizaciones-equipo',
      title: t('help.categories.administration.articles.teams.title'),
      description: t('help.categories.administration.articles.teams.description'),
      icon: '👥',
      content: getContent(organizaciones_es, organizaciones_en),
      category: 'administracion'
    },
    {
      id: 'configuracion-seguridad',
      title: t('help.categories.administration.articles.security.title'),
      description: t('help.categories.administration.articles.security.description'),
      icon: '🔒',
      content: getContent(configuracion_es, configuracion_en),
      category: 'administracion'
    },
    {
      id: 'planes-facturacion',
      title: t('help.categories.administration.articles.billing.title'),
      description: t('help.categories.administration.articles.billing.description'),
      icon: '💳',
      content: getContent(facturacion_es, facturacion_en),
      category: 'administracion'
    },
    {
      id: 'api-publica-webhooks',
      title: t('help.categories.administration.articles.api.title'),
      description: t('help.categories.administration.articles.api.description'),
      icon: '⚙️',
      content: getContent(apiPublica_es, apiPublica_en),
      category: 'administracion'
    }
  ]
})

// Mapeo de categorías con metadata
export const getCategoryMetadata = (t: TFunction): Record<string, { title: string; description: string }> => ({
  'primeros-pasos': {
    title: t('help.categories.gettingStarted.title'),
    description: t('help.categories.gettingStarted.description')
  },
  'tracking-tiempo': {
    title: t('help.categories.timeTracking.title'),
    description: t('help.categories.timeTracking.description')
  },
  'gestion-proyectos': {
    title: t('help.categories.projectManagement.title'),
    description: t('help.categories.projectManagement.description')
  },
  'reportes-analisis': {
    title: t('help.categories.reportsAnalytics.title'),
    description: t('help.categories.reportsAnalytics.description')
  },
  'dashboard': {
    title: t('help.categories.dashboard.title'),
    description: t('help.categories.dashboard.description')
  },
  'gestion-clientes': {
    title: t('help.categories.clientManagement.title'),
    description: t('help.categories.clientManagement.description')
  },
  'solucion-problemas': {
    title: t('help.categories.troubleshooting.title'),
    description: t('help.categories.troubleshooting.description')
  },
  'administracion': {
    title: t('help.categories.administration.title'),
    description: t('help.categories.administration.description')
  }
})

// Helper para obtener un artículo específico
export function getArticle(category: string, articleId: string, t: TFunction): HelpArticle | null {
  const articles = getHelpArticles(t)[category]
  if (!articles) return null
  return articles.find(a => a.id === articleId) || null
}

// Helper para obtener todos los artículos
export function getAllArticles(t: TFunction): HelpArticle[] {
  return Object.values(getHelpArticles(t)).flat()
}

// Helper para buscar artículos por término
export function searchArticles(query: string, t: TFunction): HelpArticle[] {
  if (!query.trim()) return []

  const lowerQuery = query.toLowerCase()
  return getAllArticles(t).filter(article =>
    article.title.toLowerCase().includes(lowerQuery) ||
    article.description.toLowerCase().includes(lowerQuery) ||
    article.content.toLowerCase().includes(lowerQuery)
  )
}
